import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import AgentInstructionsEditor from "datasets/pages/settings/components/AgentInstructionsEditor";
import OverviewTab from "datasets/pages/settings/components/OverviewTab";
import useInitializationStatus from "datasets/pages/settings/hooks/useInitializationStatus";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { ReadOnlyBadge } from "~/components/ReadOnlyBadge";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import rumClient from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;

const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) {
    return "";
  }

  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

export const DatasetAgentContext = () => {
  const { data: dataset } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;
  const isEnabled = dataset?.aiAgentsEnabled === true;
  const [isInitializing, setIsInitializing] = useState(false);
  const [isReinitializing, setIsReinitializing] = useState(false);

  const {
    data: initStatus,
    mutate: refreshInitStatus,
    isLoading: initStatusLoading,
  } = useInitializationStatus(dataset?.id, isEnabled);

  const triggerInitialization = async () => {
    if (!dataset) {
      return;
    }

    setIsInitializing(true);
    try {
      await apiMutate(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/initialization`,
        "POST",
        {}
      );
      refreshInitStatus();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to start dataset initialization");
    } finally {
      setIsInitializing(false);
    }
  };

  const triggerReinitialization = async () => {
    if (!dataset) {
      return;
    }

    setIsReinitializing(true);
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/agent-metadata`,
        "DELETE",
        undefined,
        { expects204: true }
      );
      await apiMutate(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/initialization`,
        "POST",
        {}
      );
      refreshInitStatus();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to reinitialize dataset");
    } finally {
      setIsReinitializing(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <h1 className="text-2xl font-semibold">Agent Context</h1>

      <section className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-medium text-foreground">Instructions</h2>
          {!isOwner && <ReadOnlyBadge />}
        </div>
        <AgentInstructionsEditor />
      </section>

      <section className="flex flex-col gap-1">
        <h2 className="text-base font-medium text-foreground">Overview</h2>
        <OverviewTab
          status={initStatus?.status}
          statusLoading={initStatusLoading}
          generatedAt={formatDate(initStatus?.generatedAt)}
          error={initStatus?.error}
          phase={initStatus?.phase}
          progress={initStatus?.progress}
          onInitialize={triggerInitialization}
          onReinitialize={triggerReinitialization}
          isInitializing={isInitializing}
          isReinitializing={isReinitializing}
        />
      </section>
    </div>
  );
};
